import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ShieldCheck, LogIn } from "lucide-react";
import { useShop } from "../context/ShopContext";
import { useAuth } from "../context/AuthContext";
import { placeOrder, formatINR, api } from "../lib/api";
import { toast } from "sonner";

export default function Checkout() {
  const { cart, subtotal, clearCart, appliedCoupon } = useShop();
  const { user } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState("");
  const [saveAddress, setSaveAddress] = useState(false);
  const [form, setForm] = useState({
    full_name: "", phone: "", email: "",
    line1: "", line2: "", city: "", state: "", pincode: "",
    payment_method: "COD",
  });

  useEffect(() => {
    if (!user) return;
    api.get("/me/addresses", { withCredentials: true }).then((r) => {
      setSavedAddresses(r.data);
      const def = r.data.find((a) => a.is_default) || r.data[0];
      if (def) {
        setSelectedAddressId(def.id);
        setForm((f) => ({
          ...f,
          full_name: def.full_name, phone: def.phone, email: def.email,
          line1: def.line1, line2: def.line2 || "", city: def.city, state: def.state, pincode: def.pincode,
        }));
      } else {
        setForm((f) => ({ ...f, email: user.email, full_name: user.name }));
      }
    }).catch(() => {});
  }, [user]);

  // Abandoned cart tracking: whenever email is present and cart has items,
  // upsert an abandoned-cart record (debounced).
  useEffect(() => {
    if (!form.email || cart.length === 0) return;
    const t = setTimeout(() => {
      api.post("/abandoned-cart", {
        email: form.email,
        items: cart.map((c) => ({ product_id: c.product_id, name: c.name, price: c.price, quantity: c.quantity, image: c.image })),
        subtotal,
      }).catch(() => {});
    }, 1500);
    return () => clearTimeout(t);
  }, [form.email, cart, subtotal]);

  const pickAddress = (a) => {
    setSelectedAddressId(a.id);
    setForm((f) => ({
      ...f, full_name: a.full_name, phone: a.phone, email: a.email,
      line1: a.line1, line2: a.line2 || "", city: a.city, state: a.state, pincode: a.pincode,
    }));
  };

  const shipping = subtotal >= 999 || subtotal === 0 ? 0 : 79;
  const discount = appliedCoupon && subtotal >= (appliedCoupon.min_order || 0)
    ? Math.round((subtotal * appliedCoupon.discount_percent) / 100 * 100) / 100
    : 0;
  const total = subtotal + shipping - discount;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (cart.length === 0) return toast.error("Cart is empty");
    setLoading(true);
    try {
      const items = cart.map((c) => ({
        product_id: c.product_id, name: c.name, price: c.price,
        quantity: c.quantity, image: c.image,
      }));
      const order = await placeOrder({
        items,
        shipping: {
          full_name: form.full_name, phone: form.phone, email: form.email,
          line1: form.line1, line2: form.line2, city: form.city, state: form.state, pincode: form.pincode,
        },
        subtotal, shipping_fee: shipping, discount, total,
        coupon_code: appliedCoupon?.code || null,
        payment_method: form.payment_method,
        save_address: user ? saveAddress : false,
      });
      clearCart();
      nav(`/order-confirmation/${order.order_number}`, { state: { order } });
    } catch (err) {
      toast.error("Could not place order. Please try again.");
    } finally { setLoading(false); }
  };

  if (cart.length === 0) {
    return (
      <div className="max-w-md mx-auto px-6 py-24 text-center">
        <p className="font-serif-brand text-3xl text-brand-charcoal">Your cart is empty</p>
        <button onClick={() => nav("/shop")} className="btn-primary mt-6">Continue Shopping</button>
      </div>
    );
  }

  return (
    <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-12" data-testid="checkout-page">
      <p className="label-tiny text-brand-gold">Almost There</p>
      <h1 className="font-serif-brand text-4xl lg:text-5xl text-brand-charcoal mt-2 mb-4">Secure Checkout</h1>

      {!user && (
        <div className="bg-brand-ivory-soft border border-brand-gold/30 p-4 mb-8 flex flex-wrap items-center justify-between gap-3" data-testid="guest-login-nudge">
          <p className="text-sm text-brand-charcoal flex items-center gap-2"><LogIn size={14} className="text-brand-maroon" /> Sign in to autofill saved addresses & track order history.</p>
          <Link to="/login" state={{ from: "/checkout" }} className="text-xs tracking-[0.2em] uppercase font-medium px-4 py-2 border border-brand-maroon text-brand-maroon hover:bg-brand-maroon hover:text-brand-ivory transition-colors duration-200">Sign In</Link>
        </div>
      )}

      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-10">
        <form onSubmit={submit} className="space-y-8" data-testid="checkout-form">
          {user && savedAddresses.length > 0 && (
            <div>
              <p className="label-tiny text-brand-gold mb-4">Deliver To</p>
              <div className="grid md:grid-cols-2 gap-3">
                {savedAddresses.map((a) => (
                  <button type="button" key={a.id} onClick={() => pickAddress(a)} className={`text-left border p-4 transition-colors duration-200 ${selectedAddressId === a.id ? "border-brand-maroon bg-brand-ivory-soft" : "border-brand-gold/30 hover:border-brand-maroon"}`} data-testid={`saved-address-${a.id}`}>
                    <p className="label-tiny text-brand-gold">{a.label}{a.is_default && " · Default"}</p>
                    <p className="font-medium text-brand-charcoal mt-1">{a.full_name}</p>
                    <p className="text-xs text-brand-muted mt-1">{a.line1}, {a.city} - {a.pincode}</p>
                  </button>
                ))}
              </div>
            </div>
          )}
          <div>
            <p className="label-tiny text-brand-gold mb-4">Contact</p>
            <div className="grid md:grid-cols-2 gap-4">
              <input required placeholder="Full Name" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className="bg-brand-ivory-soft border border-brand-gold/40 px-4 py-3 outline-none focus:border-brand-maroon" data-testid="checkout-name" />
              <input required placeholder="Phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="bg-brand-ivory-soft border border-brand-gold/40 px-4 py-3 outline-none focus:border-brand-maroon" data-testid="checkout-phone" />
              <input required type="email" placeholder="Email" value={form.email} onChange={(e) => set("email", e.target.value)} className="bg-brand-ivory-soft border border-brand-gold/40 px-4 py-3 outline-none focus:border-brand-maroon md:col-span-2" data-testid="checkout-email" />
            </div>
          </div>

          <div>
            <p className="label-tiny text-brand-gold mb-4">Shipping Address</p>
            <div className="space-y-4">
              <input required placeholder="Address Line 1" value={form.line1} onChange={(e) => set("line1", e.target.value)} className="w-full bg-brand-ivory-soft border border-brand-gold/40 px-4 py-3 outline-none focus:border-brand-maroon" data-testid="checkout-line1" />
              <input placeholder="Address Line 2 (optional)" value={form.line2} onChange={(e) => set("line2", e.target.value)} className="w-full bg-brand-ivory-soft border border-brand-gold/40 px-4 py-3 outline-none focus:border-brand-maroon" />
              <div className="grid md:grid-cols-3 gap-4">
                <input required placeholder="City" value={form.city} onChange={(e) => set("city", e.target.value)} className="bg-brand-ivory-soft border border-brand-gold/40 px-4 py-3 outline-none focus:border-brand-maroon" data-testid="checkout-city" />
                <input required placeholder="State" value={form.state} onChange={(e) => set("state", e.target.value)} className="bg-brand-ivory-soft border border-brand-gold/40 px-4 py-3 outline-none focus:border-brand-maroon" data-testid="checkout-state" />
                <input required placeholder="Pincode" value={form.pincode} onChange={(e) => set("pincode", e.target.value)} className="bg-brand-ivory-soft border border-brand-gold/40 px-4 py-3 outline-none focus:border-brand-maroon" data-testid="checkout-pincode" />
              </div>
              {user && (
                <label className="flex items-center gap-2 text-sm text-brand-charcoal mt-3">
                  <input type="checkbox" checked={saveAddress} onChange={(e) => setSaveAddress(e.target.checked)} data-testid="save-address-checkbox" />
                  Save this address for next time
                </label>
              )}
            </div>
          </div>

          <div>
            <p className="label-tiny text-brand-gold mb-4">Payment</p>
            <div className="space-y-3">
              {[
                { v: "COD", l: "Cash on Delivery", d: "Pay when it arrives (small handling fee applies)" },
                { v: "UPI", l: "UPI / Net Banking / Cards", d: "You will receive a secure payment link on email + SMS" },
              ].map((p) => (
                <label key={p.v} className={`flex gap-3 p-4 border cursor-pointer ${form.payment_method === p.v ? "border-brand-maroon bg-brand-ivory-soft" : "border-brand-gold/30"}`}>
                  <input type="radio" name="pay" value={p.v} checked={form.payment_method === p.v} onChange={(e) => set("payment_method", e.target.value)} className="mt-1" data-testid={`payment-${p.v}`} />
                  <div>
                    <p className="font-medium text-brand-charcoal">{p.l}</p>
                    <p className="text-xs text-brand-muted mt-0.5">{p.d}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <button disabled={loading} className="btn-primary w-full" data-testid="place-order-btn">
            {loading ? "Placing your order…" : `Place Order · ${formatINR(total)}`}
          </button>
          <p className="text-xs text-brand-muted flex items-center gap-2 justify-center"><ShieldCheck size={14} /> 256-bit SSL encrypted · PCI-DSS compliant</p>
        </form>

        <aside className="bg-brand-ivory-soft border border-brand-gold/30 p-6 h-fit lg:sticky lg:top-32">
          <p className="label-tiny text-brand-gold">Your Bag</p>
          <h2 className="font-serif-brand text-2xl text-brand-charcoal mt-2 mb-6">{cart.length} items</h2>
          <div className="space-y-4 max-h-80 overflow-y-auto chat-scroll pr-2">
            {cart.map((c) => (
              <div key={c.key} className="flex gap-3">
                <img src={c.image} alt="" className="w-14 h-14 object-cover" />
                <div className="flex-1 text-sm">
                  <p className="font-medium text-brand-charcoal">{c.name}</p>
                  <p className="text-xs text-brand-muted">Qty {c.quantity}{c.variant ? ` · ${c.variant}` : ""}</p>
                </div>
                <p className="text-sm font-semibold text-brand-charcoal">{formatINR(c.price * c.quantity)}</p>
              </div>
            ))}
          </div>
          <div className="gold-divider my-6" />
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-brand-muted">Subtotal</span><span>{formatINR(subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-brand-muted">Shipping</span><span>{shipping === 0 ? "Free" : formatINR(shipping)}</span></div>
            {discount > 0 && <div className="flex justify-between text-brand-maroon"><span>Discount</span><span>-{formatINR(discount)}</span></div>}
            <div className="flex justify-between font-semibold text-base pt-2 border-t border-brand-gold/25 mt-3"><span>Total</span><span className="text-brand-maroon text-lg">{formatINR(total)}</span></div>
          </div>
        </aside>
      </div>
    </div>
  );
}
